import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { FormField } from "~/components/composed/FormField";
import { TurnstileField } from "~/components/TurnstileField";
import {
  AppLink,
  AppText,
  Button,
  ButtonText,
  Card,
  Stack,
  TextField,
} from "~/components/ui";
import { AuthScreen } from "~/components/composed/AuthScreen";
import { useAuth } from "~/hooks/useAuth";
import { emailSchema } from "~/utils/form-schemas";
import { isTurnstileConfigured } from "~/utils/turnstile";

type ForgotPasswordValues = z.infer<typeof emailSchema>;

export default function ForgotPasswordScreen() {
  const { forgotPassword, forgotPasswordPending } = useAuth();
  const [sent, setSent] = useState(false);
  const turnstileEnabled = isTurnstileConfigured();
  const form = useForm<ForgotPasswordValues>({
    resolver: zodResolver(emailSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: { email: "", captchaToken: "" },
  });

  const submit = form.handleSubmit(async (values) => {
    if (turnstileEnabled && !values.captchaToken) {
      form.setError("captchaToken", { message: "Complete the spam check." });
      return;
    }
    try {
      await forgotPassword({
        email: values.email,
        captchaToken: turnstileEnabled
          ? values.captchaToken || undefined
          : undefined,
      });
      setSent(true);
    } catch (error) {
      form.setError("root", {
        message:
          error instanceof Error ? error.message : "Unable to send reset link.",
      });
    }
  });

  return (
    <AuthScreen title="Forgot password">
      <Stack>
        {sent ? (
          <Card>
            <AppText>
              If an account exists for that email, you&apos;ll receive a reset
              link shortly.
            </AppText>
          </Card>
        ) : (
          <>
            {form.formState.errors.root?.message ? (
              <AppText tone="danger" size="xs">
                {form.formState.errors.root.message}
              </AppText>
            ) : null}
            <FormField control={form.control} name="email" label="Email">
              {(field) => (
                <TextField
                  {...field}
                  value={String(field.value ?? "")}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              )}
            </FormField>
            {turnstileEnabled ? (
              <TurnstileField
                onToken={(token) =>
                  form.setValue("captchaToken", token ?? "", {
                    shouldValidate: true,
                  })
                }
              />
            ) : null}
            {form.formState.errors.captchaToken?.message ? (
              <AppText tone="danger" size="xs">
                {form.formState.errors.captchaToken.message}
              </AppText>
            ) : null}
            <Button
              onPress={() => void submit()}
              disabled={forgotPasswordPending}
            >
              <ButtonText>
                {forgotPasswordPending ? "Sending..." : "Send reset link"}
              </ButtonText>
            </Button>
          </>
        )}
        <AppLink href="/login">Back to sign in</AppLink>
      </Stack>
    </AuthScreen>
  );
}
